import { useEffect } from "react";

/**
 * Dismissal for a tooltip this tree owns itself: Escape, and the page scrolling under it (GP3,
 * AX6). One job, and it is the one job the tooltip pattern cannot get from the platform.
 */
export function useTooltipDismiss(open: boolean, dismiss: () => void): void {
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      /* preventDefault as well as the dismissal: an enclosing <dialog> fires
         its own Escape `cancel` on the same keydown, and a tooltip inside a
         modal must not take the modal down with it. */
      event.preventDefault();
      event.stopPropagation();
      dismiss();
    };

    /* GP3 — close rather than re-track. The panel is promoted into the top layer, so an ancestor
       scrolling the trigger away would otherwise leave it floating over unrelated content. */
    const onScroll = () => dismiss();
    const scrollOptions = {
      capture: true,
      passive: true
    } as const;

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("scroll", onScroll, scrollOptions);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("scroll", onScroll, scrollOptions);
    };
  }, [open, dismiss]);
}
